import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { translate } from '@jsverse/transloco';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { EventStyleArgs, SlotClassArgs } from '@progress/kendo-angular-scheduler';
import { Observable, combineLatest, map, switchMap, BehaviorSubject } from 'rxjs';
import { IBaseSearchRequest } from 'src/app/core/models/base';
import { IEmployee } from 'src/app/employee/employee';
import { EmployeeService } from 'src/app/employee/services/employee.service';
import { IHoliday } from 'src/app/setting/holiday/model/holiday';
import { HolidayService } from 'src/app/setting/holiday/services/holiday.service';
import { IRessource } from 'src/app/setting/models/ressource';
import { TenantSettingsStoreService } from 'src/app/setting/tenant-settings/services/tenant-settings-store.service';
import { getCalendarWeekFromDate } from 'src/app/shared/helpers/date-helpers';
import { TimeSpan } from 'src/app/shared/helpers/time-span';
import { AbsenceState, AbsenceType, IAbsenceGroup, ISearchAbsenceGroupsRequest } from 'src/app/time-tracking/models/absence-group';
import { AbsenceGroupService } from 'src/app/time-tracking/services/absence-group.service';
import {
  ISchedulerItem,
  ISearchSchedulerItemRequest,
} from '../../scheduler/models/scheduler';
import { ResourcePlanerService } from '../../scheduler/services/resource-planer.service';
import { PermissionStorageService } from 'src/app/authentication/storages/permission-storage.service';

@UntilDestroy()
@Component({
    selector: 'app-home',
    templateUrl: './home.component.html',
    styleUrls: ['./home.component.scss'],
    standalone: false
})
export class HomeComponent implements OnInit {
  constructor(
    private schedulerService: ResourcePlanerService,
    private route: ActivatedRoute,
    private employeeService: EmployeeService,
    private holidayService: HolidayService,
    private absenceGroupService: AbsenceGroupService,
    private tenantSettingsStore: TenantSettingsStoreService,
    private permissionStore: PermissionStorageService
  ) {}

  employee: IEmployee;

  ressource: IRessource;

  holidays: IHoliday[];

  weekNumber:number = getCalendarWeekFromDate(new Date());
  weekNumberEnd:number;
  public toolbarWidth: number;

  events$ = new BehaviorSubject<ISchedulerItem[]>([]);
  absenceGroups$ = new BehaviorSubject<IAbsenceGroup[]>([]);
  tenantSettings$ = this.tenantSettingsStore.tenantSettings$;

  isLoading = false;

  get absenceStates() {
    return AbsenceState;
  }

  
  get absenceTypes() {
    return AbsenceType;
  }

  listSchedulerItemsRequest: ISearchSchedulerItemRequest = {
    ressourceId: null,
    start: new Date(),
    pageNumber: 0,
    pageSize: 999999,
  };

  listHolidayRequest: IBaseSearchRequest = {
    pageNumber: 0,
    pageSize: 999999,
    advancedFilter: {
      logic: 'and',
      filters: [
        {
          field: 'start',
          operator: 'gte',
          value: new Date(new Date().setUTCHours(0, 0, 0, 0)),
        },
        {
          field: 'end',
          operator: 'lte',
          value: new Date(new Date().setUTCHours(23, 59, 59, 999)),
        },
      ],
    },
  };

  listAbsenceGroupRequest : ISearchAbsenceGroupsRequest = {
    pageNumber: 0,
    pageSize: 999999,
    start: new Date(new Date().setUTCHours(0,0,0,0)),
    end: new Date(new Date().setUTCHours(23,59,59,999))
  }

  ngOnInit(): void {
    // this.employee = this.route.snapshot.data["employee"] as IEmployee;
    this.loadCurrentEmployee();
  }

  loadCurrentEmployee() {
    this.employeeService.getEmployeeDetails().subscribe((response) => {
      this.employee = response;
      this.listSchedulerItemsRequest.ressourceId = this.employee.ressource.id;
      this.listAbsenceGroupRequest.employeeId = this.employee.id;
    });
  }

  public selectedDate: Date = new Date();

  

  loadEvents() {
    Promise.resolve().then(() => this.isLoading = true);
    this.listAbsenceGroupRequest.start = this.listSchedulerItemsRequest.start;
    this.listAbsenceGroupRequest.end = this.listSchedulerItemsRequest.end;

    if (this.hasPermission('Permissions.Holidays.Search')) {
      this.listHolidayRequest.advancedFilter.filters[0].value =
        this.listSchedulerItemsRequest.start;
      this.listHolidayRequest.advancedFilter.filters[1].value =
        this.listSchedulerItemsRequest.end;

      this.holidayService
        .searchHolidays(this.listHolidayRequest)
        .pipe(untilDestroyed(this))
        .subscribe((response) => {
          this.holidays = response.data;
        });
    }

    this.schedulerService
      .listEvents(this.listSchedulerItemsRequest)
      .pipe(untilDestroyed(this))
      .subscribe((response) => {
        this.events$.next(response.data);
        this.isLoading = false;
      });

    // Own absences only (employeeId is the current employee), so the base right suffices.
    if (this.hasPermission('Permissions.AbsenceGroups.Search')) {
      this.absenceGroupService.searchAbsenceGroups(this.listAbsenceGroupRequest)
      .pipe(untilDestroyed(this))
      .subscribe(response => {
        this.absenceGroups$.next(response.data);
      })
    }
  }

  private hasPermission(permission: string): boolean {
    const permissions = this.permissionStore.getPermissions();
    return !!permissions?.find(p => p === permission);
  }

  onDateChange(event) {
    this.listSchedulerItemsRequest.start = event.dateRange.start;
    this.listSchedulerItemsRequest.end = event.dateRange.end;
    this.loadEvents();

    this.weekNumber = getCalendarWeekFromDate(event.dateRange.start);
    this.weekNumberEnd = getCalendarWeekFromDate(event.dateRange.end);
  }

  setSlotBackgroundColor = (args: SlotClassArgs) => {
    let activeHoliday = this.holidays?.filter(
      (holiday) =>{
        const holidayDateString =  new Date(holiday.start).toDateString();
        const argsDateString = args.start.toDateString();
       return holidayDateString == argsDateString;
      }
    );
    if (Array.isArray(activeHoliday) && activeHoliday.length) {
      return 'holiday';
    }
  };

  schedulerEvents$: Observable<ISchedulerItem[]> = combineLatest([
    this.events$,
    this.absenceGroups$,
    this.tenantSettings$
  ]).pipe(
    map(([events, absenceGroups, tenantSettings]) => ({
      events,
      absenceGroups,
      tenantSettings
    })),
    switchMap(({ events, absenceGroups, tenantSettings }) => {
      let schedulerEvents: ISchedulerItem[] = [];

      
      events.map((event) => {
        for(var loopDate = new Date(event.start); loopDate <= new Date(event.end);loopDate.setDate(loopDate.getDate() + 1)){

          const newEvent: ISchedulerItem = {
            title: event.title,
            start: new Date(event.start).setHours(0,0,0,0) == new Date(loopDate).setHours(0,0,0,0) ? new Date(event.start) : new Date(new Date(loopDate).setHours(0,0,0,0)),
            end: new Date(event.end).setHours(0,0,0,0) == new Date(loopDate).setHours(0,0,0,0) ? new Date(event.end) : new Date(new Date(loopDate).setHours(23, 59, 59, 999)),
            ressourceIds: event.ressourceIds,
            ressources: event.ressources,
            description: event.description,
            isAllDay: event.isAllDay,
            recurrenceRule: event.recurrenceRule,
            timeEntryCategoryId: event.timeEntryCategoryId,
            timeEntryCategory: event.timeEntryCategory,
            employeeIds: event.employeeIds,
            employees: event.employees,
            assignmentId: event.assignmentId,
            assignment: event.assignment,
            id: event.id
          };
  
          schedulerEvents.push(newEvent);
        }
        
      });

      absenceGroups.map((absenceGroup) => {
        if (absenceGroup.absenceState != this.absenceStates.Declined) {

          absenceGroup.absences.forEach(absence => {

            let startTime = new Date(absence.date);
            let endTime = new Date(
              new Date(absence.date).setUTCHours(23, 59, 59, 999)
            );

             // absence in the morning
            if(absence.isHalfDayEnd){
              const dayEndTimeSpanObj = TimeSpan.parse(tenantSettings.endMorningWorkTime).getAsTimeObject();
              endTime = new Date(
                new Date(absenceGroup.dateTo).setHours(dayEndTimeSpanObj.hours, dayEndTimeSpanObj.minutes)
              );
            } 
            if ( absence.isHalfDayStart){
              // absence in the afternoon
              const dayStartTimeSpanObj = TimeSpan.parse(tenantSettings.startAfternoonWorkTime).getAsTimeObject();
              startTime = new Date(
                new Date(absenceGroup.dateFrom).setHours(dayStartTimeSpanObj.hours, dayStartTimeSpanObj.minutes)
              );
            }

            const newEvent: ISchedulerItem = {
              title: `${translate(
                'absenceGroups.absenceTypes.' +
                  this.absenceTypes[absenceGroup.absenceType]
              )} (${translate(
                'absenceGroups.absenceStates.' +
                  this.absenceStates[absenceGroup.absenceState]
              )})
               ${absenceGroup?.title ?? ''}`,
              start: startTime,
              end: endTime,
              description: `${translate(
                'absenceGroups.absenceStates.' +
                  this.absenceStates[absenceGroup.absenceState]
              )}
               ${absenceGroup?.title ?? ''}`,
              employeeIds: [absenceGroup.employee.id],
              employees: [absenceGroup.employee],
              ressourceIds: [absenceGroup.employee.ressource.id],
              ressources: [absenceGroup.employee.ressource],
              absenceType: absenceGroup.absenceType,
              id: absenceGroup.id
            };
  
            schedulerEvents.push(newEvent);
          });          
        }
      });

      return [schedulerEvents];
    })
  );

  setBorderColor = (args: EventStyleArgs) => {
    let ngStyleObj: Object;

      if (args.event.dataItem?.absenceType == this.absenceTypes.Compensation) {
        ngStyleObj = {
          'background-color': '#fd9f3e',
        };
      } else if (args.event.dataItem?.absenceType != null) {
        ngStyleObj = {
          'background-color': 'red',
        };
      } else {
        ngStyleObj = {
          'background-color': `${args.event.dataItem?.timeEntryCategory?.color}`,
        };
    }

    return ngStyleObj;
  };

  public onSchedulerResize(width: number): void {
    this.toolbarWidth = width;
  }
}
